/* eslint-disable max-lines */
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, SxProps, Theme, useTheme } from '@mui/material';
import MuiPagination from '@mui/material/Pagination';
import {
  DataGrid,
  GridCallbackDetails,
  GridColDef,
  GridColumns,
  GridEventListener,
  GridFeatureMode,
  GridPagination,
  GridRenderCellParams,
  GridRow,
  GridRowIdGetter,
  GridRowModel,
  GridRowParams,
  GridRowProps,
  GridSelectionModel,
  GridSortingInitialState,
  GridSortModel,
  GridValidRowModel,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import { GridInitialStateCommunity } from '@mui/x-data-grid/models/gridStateCommunity';

import { NO_SELECTION, ROWS_PER_PAGE_OPTIONS } from './constants';
import { LoadingComponent, RenderEmptyState } from './OverlayStates';
import { tableStyleSx } from './style';

export type { GridColDef, GridRenderCellParams, GridValueFormatterParams };

export const Table = <RowData extends GridValidRowModel>({
  testId,
  columns,
  defaultSort,
  disableSelection,
  error,
  getRowId,
  hasPagination,
  hideFooter,
  isSelected = true,
  loading,
  onRowClick,
  onSortModelChange,
  page,
  pageSize,
  paginationMode,
  paginationSize = 'medium',
  renderEmptyText,
  rowCount,
  rows,
  rowsPerPageOptions,
  setPage,
  setPageSize,
  size = 'small',
  sortingMode,
  hasMultiSelection = false,
  onSelectionModelChange,
  selectionModel,
  customStyles,
  isRowSelectable,
}: {
  testId?: string;
  columns: GridColumns<RowData>;
  disableSelection?: boolean;
  error: boolean;
  getRowId?: GridRowIdGetter<RowData>;
  hasPagination?: true | undefined;
  hideFooter?: boolean;
  isSelected?: boolean;
  loading: boolean;
  onRowClick?: GridEventListener<'rowClick'> | undefined;
  onSortModelChange?:
    | ((model: GridSortModel, details: GridCallbackDetails<any>) => void)
    | undefined;
  page?: number;
  pageSize?: number;
  paginationMode?: GridFeatureMode;
  paginationSize?: 'medium' | 'small' | 'large';
  renderEmptyText: string | ReactNode;
  rowCount?: number;
  rows: GridRowModel<RowData>[];
  rowsPerPageOptions?: number[];
  setPage?: (page: number) => void;
  setPageSize?: (page: number) => void;
  size?: 'small' | 'medium';
  sortingMode?: GridFeatureMode | undefined;
  defaultSort?: GridSortingInitialState;
  hasMultiSelection?: boolean;
  onSelectionModelChange?: (selectionModel: GridSelectionModel) => void;
  selectionModel?: GridSelectionModel;
  customStyles?: SxProps<Theme>;
  isRowSelectable?: (params: GridRowParams<RowData>) => boolean;
}) => {
  const theme = useTheme();

  const memoizedLoadingState = useCallback(
    () => <LoadingComponent columns={columns} />,
    [columns],
  );
  const memoizedNoResults = useCallback(
    () => <RenderEmptyState text={renderEmptyText} />,
    [renderEmptyText],
  );

  const [selectedRow, setSelectedRow] = useState<
    GridSelectionModel | typeof NO_SELECTION
  >();

  useEffect(() => {
    if (rows.length) {
      const initialSelectedRow = rows.find((val: any) => !!val.select)?.id;
      setSelectedRow(initialSelectedRow);
    }
  }, [rows]);

  const Pagination = useCallback(() => {
    if (
      typeof page === 'number' &&
      typeof pageSize === 'number' &&
      typeof rowCount === 'number' &&
      typeof setPage === 'function'
    ) {
      return (
        <MuiPagination
          color="primary"
          count={Math.ceil(rowCount / pageSize)}
          page={page + 1}
          onChange={(e, v) => setPage(v - 1)}
          size={paginationSize}
        />
      );
    }
    return null;
  }, [page, pageSize, rowCount, setPage, paginationSize]);

  const CustomPagination = useCallback(
    (props: any) => {
      return <GridPagination ActionsComponent={Pagination} {...props} />;
    },
    [Pagination],
  );

  const initialState = useMemo(() => {
    const state: GridInitialStateCommunity = {};
    if (defaultSort) {
      state.sorting = defaultSort;
    }
    return state;
  }, [defaultSort]);

  const CustomGridRow = useCallback(
    (props: GridRowProps) => (
      <GridRow
        {...props}
        data-testid={testId ? `${testId}.row.${props.index}` : undefined}
      />
    ),
    [testId],
  );

  return (
    <Box sx={{ width: '100%' }} data-testid={testId}>
      <DataGrid
        rows={!error && !loading ? rows : []}
        rowHeight={size === 'small' ? 52 : 72}
        columns={columns}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        rowsPerPageOptions={rowsPerPageOptions ?? ROWS_PER_PAGE_OPTIONS}
        pagination={hasPagination}
        disableColumnMenu
        hideFooter={hideFooter}
        hideFooterSelectedRowCount
        rowCount={rowCount}
        autoHeight
        disableSelectionOnClick={disableSelection}
        initialState={initialState}
        loading={loading}
        components={{
          LoadingOverlay: memoizedLoadingState,
          NoRowsOverlay: memoizedNoResults,
          NoResultsOverlay: memoizedNoResults,
          Pagination: CustomPagination,
          Row: CustomGridRow,
        }}
        paginationMode={paginationMode}
        onSelectionModelChange={(select) => {
          setSelectedRow(select);
          onSelectionModelChange?.(select);
        }}
        selectionModel={
          selectionModel ?? (isSelected ? selectedRow : NO_SELECTION)
        }
        sx={{
          ...tableStyleSx({ loading, onRowClick, theme }),
          ...customStyles,
        }}
        onRowClick={onRowClick}
        sortingMode={sortingMode}
        onSortModelChange={onSortModelChange}
        getRowId={getRowId}
        checkboxSelection={hasMultiSelection}
        isRowSelectable={isRowSelectable}
      />
    </Box>
  );
};
